import MapElement from './MapElement';
import FeatureDrawer from '../helpers/FeatureDrawer';
import VectorLayer from 'ol/layer/Vector';
import Point from 'ol/geom/Point';

import * as olExtent from 'ol/extent';
import 'ol/ol.css';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';

import LayerGroup from 'ol/layer/Group';
import WebGLPointsLayer from 'ol/layer/WebGLPoints';
import WebGLVectorLayerRenderer from 'ol/renderer/webgl/VectorLayer';

import Layer from 'ol/layer/Layer';

import MotionplanColors from '../helpers/MotionplanColors';

import { Stroke, Circle, Fill, Style, Icon } from 'ol/style.js';
import { MultiPoint } from "ol/geom";

// current tablet map size is around 660 x 530. 
// this values is the distance in pixels between each arrow
const arrowDistanceConstant = 200;
const arrowStartPointConstant = 50;

class WebGLLayer extends Layer {
    constructor(options) {
        super(options);
        this.style = options.style;
    }

    createRenderer() {
        return new WebGLVectorLayerRenderer(this, {
            className: this.getClassName(),
            style: this.style,
        });
    }
}

export default class MapWebGLMotionplan extends MapElement {
    constructor(map, id, mapElementSettings, parent) {
        super(map, id, mapElementSettings, parent)
        this.parentId = parent.id;
        this.motionplan = null;
        this.source = null;

        this.pointSource = null;
        this.lineSource = null;

        this.pointLayer = null;
        this.lineLayer = null;

        this.selectSource = null;
        this.selectLayer = null;

        this.connectedSource = null;
        this.connectedLayer = null;

        this.noKey = true;

        this.isSelected = false;

        this.pointStyle;
        this.lineStyle;
        this.isLocalMp = false;
    }

    drawLayer(motionplan, projection, isLocalMp = false) {
        this.projection = projection;
        this.motionplan = motionplan;
        this.isLocalMp = isLocalMp;

        this.setLayers()

        this.setSources()

        if (!this.mapElementSettings.defaultOn) {
            this.hide();
        }
    }

    draw() {
        this.setSources()
    }

    setSources() {
        this.lineSource.clear();
        this.pointSource.clear();
        this.selectSource.clear();

        var featureDrawer = new FeatureDrawer(this.projection, this.mapModel.projection);

        const selectLinePoints = [];

        let lastSegment;
        this.motionplan.data.forEach((segment, index) => {
            let feature = null;
            if (lastSegment) {
                if (segment.Curvature != 0) {
                    const points = [];

                    let vertex = { x: lastSegment.X, y: lastSegment.Y }
                    if (this.projection != null) {
                        vertex = featureDrawer.transformCoordinates(vertex);
                    }

                    points.push([vertex.x, vertex.y], ...this.generateArc(segment).map(point => {
                        let vertex = { x: point[0], y: point[1] }
                        if (this.projection != null) {
                            vertex = featureDrawer.transformCoordinates(vertex);
                        }
                        selectLinePoints.push([vertex.x, vertex.y])
                        return [vertex.x, vertex.y]
                    }))

                    const arc = new LineString(points);
                    feature = new Feature(arc);
                } else {
                    let vertex = { x: segment.X, y: segment.Y }
                    if (this.projection != null) {
                        vertex = featureDrawer.transformCoordinates(vertex);
                    }
                    selectLinePoints.push([vertex.x, vertex.y])

                    feature = featureDrawer.drawFeature(
                        {
                            type: "LINE",
                            start: { x: lastSegment.X, y: lastSegment.Y },
                            end: { x: segment.X, y: segment.Y }
                        }
                    );
                }
                feature.set("arguments", segment.functionArray);
                feature.set("motionplanSegmentIndex", index);
                feature.set("motionplanElementId", this.id);
                this.setFeatureValues(feature, this.isSelected)

                this.lineSource.addFeature(feature);
            } else {
                let vertex = { x: segment.X, y: segment.Y }
                if (this.projection != null) {
                    vertex = featureDrawer.transformCoordinates(vertex);
                }
                selectLinePoints.push([vertex.x, vertex.y])
            }

            let featurePoint = featureDrawer.drawFeature({ type: "POINT", x: segment.X, y: segment.Y })
            featurePoint.set("arguments", segment.functionArray);
            featurePoint.set("motionplanSegmentIndex", index);
            featurePoint.set("motionplanElementId", this.id);

            this.setFeatureValues(featurePoint, this.isSelected)

            if (index == 0 || index == this.motionplan.data.length - 1) {
                featurePoint.set("endPoint", 1);
            } else {
                featurePoint.set("endPoint", 0);
            }

            this.pointSource.addFeature(featurePoint);

            lastSegment = segment;
        });

        const line = new LineString(selectLinePoints);
        const selectLine = new Feature(line);
        selectLine.set("motionplanElementId", this.id);
        this.selectSource.addFeature(selectLine);
    }

    checkForMotionplanFunctionAndOrArgument(ft, id, arg = null) {
        if (arg !== null) {
            return ft.get("arguments").some(s => {
                return (s.IdentifierFunction == id && s.Arguments[0] == arg)
            })
        } else {
            return ft.get("arguments").some(s => {
                return (s.IdentifierFunction == id)
            })
        }
    }

    setFeatureValues(ft, selected) {
        if (selected) {
            ft.set("selected", 1)
        } else {
            ft.set("selected", 0)
        }
        if (ft.getGeometry().getType() == "Point") {
            if (this.checkForMotionplanFunctionAndOrArgument(ft, 1)) {
                ft.set("pointType", "spray")
            } else if (this.checkForMotionplanFunctionAndOrArgument(ft, 2)) {
                ft.set("pointType", "drill")
            } else if (this.checkForMotionplanFunctionAndOrArgument(ft, 3)) {
                ft.set("pointType", "stop")
            } else {
                ft.set("pointType", "drive")
            }
        } else { // lines
            if (this.checkForMotionplanFunctionAndOrArgument(ft, 51, 1)) {
                ft.set("lineType", "spray")
            } else if (this.checkForMotionplanFunctionAndOrArgument(ft, 51, 0)) {
                ft.set("lineType", "drive")
            } else {
                ft.set("lineType", "drive") // maybe another default 
            }
        }
    }

    setLayers() {
        if (!this.layer) {
            this.layer = new LayerGroup({
                parentId: this.parent.id,
                layers: [],
                zIndex: 0
            });

            this.setStyle()

            this.lineSource = new VectorSource()
            this.lineLayer = new WebGLLayer({
                parentId: this.parent.id,
                source: this.lineSource,
                style: this.lineStyle,
                zIndex: 1
            });
            this.layer.getLayers().push(this.lineLayer);

            this.pointSource = new VectorSource();
            this.pointLayer = new WebGLPointsLayer({
                parentId: this.parent.id,
                source: this.pointSource,
                style: this.pointStyle,
                zIndex: 2
            });
            this.layer.getLayers().push(this.pointLayer);

            var c;
            if (this.mapModel.dark) {
                c = "rgba(0,0,0,0.1)"
            } else {
                c = "rgba(255,255,255,0.1)"
            }

            this.selectSource = new VectorSource()
            this.selectLayer = new VectorLayer({
                parentId: this.parent.id,
                source: this.selectSource,
                zIndex: 0,
                style: new Style({
                    stroke: new Stroke({
                        width: 0.1,
                        color: c
                    })
                })
            })
            this.layer.getLayers().push(this.selectLayer);

            const localMpStyle = (ft, res, dark) => {
                const styles = []

                if (res < 0.1) {
                    var geometry = ft.getGeometry();

                    var distanceBetweenPoints = res * arrowDistanceConstant;
                    var distanceAlongLine = res * arrowStartPointConstant;
                    var totalLength = 0;

                    geometry.forEachSegment((start, end) => {
                        var dx = end[0] - start[0];
                        var dy = end[1] - start[1];

                        var segmentLength = Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
                        totalLength += segmentLength;

                        var rotation = Math.atan2(dy, dx);
                        var points = [];


                        while (totalLength > distanceAlongLine) {
                            var pointOnLine = [
                                start[0] + (end[0] - start[0]) * (distanceAlongLine + segmentLength - totalLength) / segmentLength,
                                start[1] + (end[1] - start[1]) * (distanceAlongLine + segmentLength - totalLength) / segmentLength
                            ];
                            points.push(pointOnLine);

                            distanceAlongLine += distanceBetweenPoints;
                        }
                        if (points.length > 0) {
                            styles.push(new Style({
                                geometry: new MultiPoint(points),
                                image: new Icon({
                                    src: 'images/arrow-white.png',
                                    anchor: [0.75, 0.5],
                                    rotateWithView: true,
                                    rotation: -rotation,
                                    color: dark ? "#0099FF" : "#0099FF",
                                    scale: 0.7
                                }),
                            }));
                        }
                    })
                }

                return styles;
            }

            const globalMpStyle = (ft, res, dark) => {
                const styles = []
                const geometry = ft.getGeometry();
                const coords = geometry.getCoordinates();
                // const firstSegment = [coords[0], coords[1]];
                // const lastSegment = [coords[coords.length - 1], coords[coords.length - 2]];

                const firstPoint = coords[0]
                const lastPoint = coords[coords.length - 1]

                // var dx, dy, rotation

                // dx = firstSegment[1][0] - firstSegment[0][0];
                // dy = firstSegment[1][1] - firstSegment[0][1];

                // rotation = Math.atan2(dy, dx);


                // styles.push(new Style({
                //     geometry: new Point(coords[0]),
                //     image: new Icon({
                //         src: 'images/xandertestpijltje3.png',
                //         anchor: [0.75, 0.5],
                //         rotateWithView: true,
                //         rotation: -rotation,
                //         color: "#4caf50",
                //         scale: 1.3
                //     }),
                // }));

                // dx = lastSegment[1][0] - lastSegment[0][0];
                // dy = lastSegment[1][1] - lastSegment[0][1];

                // rotation = Math.atan2(dy, dx);

                // styles.push(new Style({
                //     geometry: new Point(coords[coords.length - 1]),
                //     image: new Icon({
                //         src: 'images/xandertestpijltje3.png',
                //         anchor: [0.75, 0.5],
                //         rotateWithView: true,
                //         rotation: -rotation,
                //         color: "#4caf50",
                //         scale: 1.3
                //     }),
                // }));


                // return styles



                return [
                    // ...styles,
                    new Style({
                        geometry: new MultiPoint([firstPoint]),
                        image: new Circle({
                            fill: new Fill({
                                color: "#4caf50" // "#00008c"
                            }),
                            radius: 8,
                        }),
                    }), new Style({
                        geometry: new MultiPoint([lastPoint]),
                        image: new Circle({
                            fill: new Fill({
                                color: "#4caf50" // "#00008c"
                            }),
                            radius: 8,
                        }),
                    })]
            }

            const lcmpLayer = new VectorLayer({
                parentId: this.parent.id,
                source: this.selectSource,
                zIndex: this.isLocalMp ? 12 : 0,
                style: this.isLocalMp ? localMpStyle : globalMpStyle
            })

            this.layer.getLayers().push(lcmpLayer);

            const connectedStyle = (feature) => {
                const geomType = feature.getGeometry().getType();
                if (geomType === 'LineString') {
                    const styles = [new Style({
                        stroke: new Stroke({
                            color: '#808080',  // Gray color for the line
                            width: 1           // Width of the line
                        })
                    })];
                    const coordinates = feature.getGeometry().getCoordinates();

                    if (coordinates.length > 0) {
                        // Start point
                        styles.push(new Style({
                            geometry: new Point(coordinates[0]),
                            image: new Circle({
                                fill: new Fill({
                                    color: '#ff0000'  // Red color for the endpoint
                                }),
                                radius: 5,           // Size of the endpoint dot
                                stroke: new Stroke({
                                    color: '#ff0000', // Red border for the endpoint dot
                                    width: 2
                                })
                            })
                        }));

                        // End point
                        styles.push(new Style({
                            geometry: new Point(coordinates[coordinates.length - 1]),
                            image: new Circle({
                                fill: new Fill({
                                    color: '#ff0000'  // Red color for the endpoint
                                }),
                                radius: 4,           // Size of the endpoint dot
                                stroke: new Stroke({
                                    color: '#ff0000', // Red border for the endpoint dot
                                    width: 2
                                })
                            })
                        }));
                    }

                    return styles;
                } else if (geomType === 'Point') {
                    return new Style({
                        image: new Circle({
                            fill: new Fill({
                                color: '#ff0000'  // Red color for the point
                            }),
                            radius: 4,           // Size of the point dot
                            stroke: new Stroke({
                                color: '#ff0000', // Red border for the point dot
                                width: 2
                            })
                        })
                    });
                }

                return null;
            };

            this.connectedSource = new VectorSource()
            this.connectedLayer = new VectorLayer({
                parentId: this.parent.id,
                source: this.connectedSource,
                zIndex: 15,
                style: connectedStyle
            })
            this.layer.getLayers().push(this.connectedLayer);

            this.parent.layer.getLayers().push(this.layer);

            // this.refreshLayer()
        }
    }

    getLayers() {
        if (!this.layer) {
            return null;
        }
        const layerObject = {
            id: this.parent.id + "-" + this.id,
            name: this.motionplan.name ? this.motionplan.name : this.id,
            editButton: this.mapElementSettings.editButton,
            actions: this.mapElementSettings.actions,
            checkBoxValue: false,
            visible: this.visible,
            disabled: this.disabled,
            show: this.show.bind(this),
            hide: this.hide.bind(this),
            children: []
        };
        return layerObject
    }

    getExtent() {
        if (this.layer) return this.layer.getLayers().getArray()[0].getSource().getExtent();
        return null;
    }

    getFeatureFromPoint(coordinates) {
        const pointFeatures = this.pointSource.getFeatures()
        const lineFeatures = this.lineSource.getFeatures()

        var closestFeature;
        var closestDistance = Infinity;

        // put pointFeatures first, this way if a point is closest the point gets selected first. i think this is better.
        const features = [...pointFeatures, ...lineFeatures]

        features.forEach(feature => {
            const closestPoint = feature.getGeometry().getClosestPoint(coordinates)

            // Calculate Euclidean distance using Pythagorean theorem
            const dx = closestPoint[0] - coordinates[0];
            const dy = closestPoint[1] - coordinates[1];
            const distance = Math.sqrt(dx * dx + dy * dy); // Pythagorean distance

            // Check if this distance is smaller than the current minimum distance
            if (distance < closestDistance) {
                closestDistance = distance;
                closestFeature = feature; // Update the closest feature
            }
        })

        return closestFeature
    }

    /**
     * @param {boolean} dash Include line dash
     * @param {boolean} pattern Include image pattern
     * @return {import('ol/style/webgl.js').WebGLStyle} Generated style
     */
    setStyle() {
        this.lineStyle = [
            {
                variables: {
                    dark: Number(this.mapModel.dark) // dark should be cast to int because styles are weird
                },
                "stroke-width":
                    [
                        "case",
                        ["==", ["get", "selected"], 1],
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", 4,
                            "drive", 3,
                            3],
                        0
                    ],
                "stroke-color": "#FFFFFF", // motionplancolor selected background
                'stroke-miter-limit': 10,
                'stroke-line-cap': "butt",
                'stroke-line-join': "miter",
                'stroke-line-dash':
                    [
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", 0,
                            "drive", 20,
                            0],
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", 0,
                            "drive", 10,
                            0],

                    ]
            },
            {
                variables: {
                    dark: Number(this.mapModel.dark) // dark should be cast to int because styles are weird
                },
                "stroke-width": ["match",
                    ["get", "lineType"],                   // Feature parameter for color
                    "spray", 2,
                    "drive", 1,
                    2],
                "stroke-color": [
                    "case",
                    ["==", ["get", "selected"], 1],
                    ["case",
                        ["==", ["var", "dark"], 1],
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", MotionplanColors.line.spray.selected.dark,
                            "drive", MotionplanColors.line.drive.selected.dark,
                            "#FFFFFF"], // When dark and selected
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", MotionplanColors.line.spray.selected.default,
                            "drive", MotionplanColors.line.drive.selected.default,
                            "#000000"] // When selected but not dark
                    ],
                    [
                        "case",
                        ["==", ["var", "dark"], 1],
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", MotionplanColors.line.spray.dark,
                            "drive", MotionplanColors.line.drive.dark,
                            "#FFFFFF"], // When dark but not selected
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", MotionplanColors.line.spray.default,
                            "drive", MotionplanColors.line.drive.default,
                            "#000000"] // When not selected and not dark
                    ]
                ],
                'stroke-miter-limit': 10,
                'stroke-line-cap': "butt",
                'stroke-line-join': "miter",
                'stroke-line-dash':
                    [
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", 0,
                            "drive", 20,
                            0],
                        ["match",
                            ["get", "lineType"],                   // Feature parameter for color
                            "spray", 0,
                            "drive", 10,
                            0],
                    ]
            }
        ]

        this.pointStyle = {
            variables: {
                dark: Number(this.mapModel.dark),
            },
            "circle-opacity": 1,
            "circle-radius":
                ["match",
                    ["get", "pointType"],                   // Feature parameter for color
                    "spray", 4,
                    "drill", 4,
                    "drive", 2,
                    "stop", 4,
                    4                       // Default color
                ], //["get", "radius"],
            "circle-stroke-color":
                [
                    "case",
                    ["==", ["get", "selected"], 1],
                    ["case",
                        ["==", ["var", "dark"], 1],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.selected.stroke.dark,
                            "drill", MotionplanColors.point.drill.selected.stroke.dark,
                            "drive", MotionplanColors.point.drive.selected.stroke.dark,
                            "stop", MotionplanColors.point.stop.selected.stroke.dark,
                            "#000000"                           // Default color
                        ],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.selected.stroke.default,
                            "drill", MotionplanColors.point.drill.selected.stroke.default,
                            "drive", MotionplanColors.point.drive.selected.stroke.default,
                            "stop", MotionplanColors.point.stop.selected.stroke.default,
                            "#000000"                           // Default color
                        ],
                    ],
                    [
                        "case",
                        ["==", ["var", "dark"], 1],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.stroke.dark,
                            "drill", MotionplanColors.point.drill.stroke.dark,
                            "drive", MotionplanColors.point.drive.stroke.dark,
                            "stop", MotionplanColors.point.stop.stroke.dark,
                            "#000000"                           // Default color
                        ],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.stroke.default,
                            "drill", MotionplanColors.point.drill.stroke.default,
                            "drive", MotionplanColors.point.drive.stroke.default,
                            "stop", MotionplanColors.point.stop.stroke.default,
                            "#000000"                           // Default color
                        ],
                    ]
                ],
            "circle-fill-color":
                [
                    "case",
                    ["==", ["get", "selected"], 1],
                    ["case",
                        ["==", ["var", "dark"], 1],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.selected.fill.dark,
                            "drill", MotionplanColors.point.drill.selected.fill.dark,
                            "drive", MotionplanColors.point.drive.selected.fill.dark,
                            "stop", MotionplanColors.point.stop.selected.fill.dark,
                            "#000000"                           // Default color
                        ],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.selected.fill.default,
                            "drill", MotionplanColors.point.drill.selected.fill.default,
                            "drive", MotionplanColors.point.drive.selected.fill.default,
                            "stop", MotionplanColors.point.stop.selected.fill.default,
                            "#000000"                           // Default color
                        ],
                    ],
                    [
                        "case",
                        ["==", ["var", "dark"], 1],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.fill.dark,
                            "drill", MotionplanColors.point.drill.fill.dark,
                            "drive", MotionplanColors.point.drive.fill.dark,
                            "stop", MotionplanColors.point.stop.fill.dark,
                            "#000000"                           // Default color
                        ],
                        [
                            "match",
                            ["get", "pointType"],                   // Feature parameter for color
                            "spray", MotionplanColors.point.spray.fill.default,
                            "drill", MotionplanColors.point.drill.fill.default,
                            "drive", MotionplanColors.point.drive.fill.default,
                            "stop", MotionplanColors.point.stop.fill.default,
                            "#000000"                           // Default color
                        ],
                    ]
                ],
            "circle-stroke-width": 1,
        }
    }

    deselectMotionplan() {
        // if (this.isSelected) {
            this.layer.setZIndex(0);
            this.lineLayer.setZIndex(1);
            this.pointLayer.setZIndex(2);
            this.isSelected = false;

            [...this.pointSource.getFeatures(), ...this.lineSource.getFeatures(), ...this.connectedSource.getFeatures()].forEach(ft => {
                this.setFeatureValues(ft, false)
            })
        // }
        // this.refreshLayer()
    }

    selectMotionplan() {
        if (!this.isSelected) {
            this.layer.setZIndex(10);
            this.lineLayer.setZIndex(11);
            this.pointLayer.setZIndex(13);
            this.isSelected = true;

            const selectedFeatures = [...this.pointSource.getFeatures(), ...this.lineSource.getFeatures()]

            // check if this still works when implementing multiple mps on tmi
            if (this.parent.selectInteraction) {
                this.parent.selectInteraction.getFeatures().clear();
            }

            selectedFeatures.forEach(ft => {
                this.setFeatureValues(ft, true)
                if (this.parent.selectInteraction) {
                    this.parent.selectInteraction.getFeatures().push(ft);
                }
            })

            if (this.parent.selectInteraction && this.connectedSource.getFeatures().length > 0) {
                this.parent.selectInteraction.getFeatures().push(...this.connectedSource.getFeatures());
            }
        }
    }

    selectSegment(segmentIndex) {
        this.isSelected = true
        this.deselectMotionplan()

        var selectedFeatures = [...this.pointSource.getFeatures(), ...this.lineSource.getFeatures()].filter(feature => {
            return feature.get("motionplanSegmentIndex") == segmentIndex
        });

        this.parent.selectInteraction.getFeatures().clear();
        if (selectedFeatures) {
            selectedFeatures.forEach(feature => {
                this.setFeatureValues(feature, true)
                this.parent.selectInteraction.getFeatures().push(feature);
            })

            const totalExtend = selectedFeatures.reduce((te, feature) => {
                if (te) {
                    return olExtent.extend(te, feature.getGeometry().getExtent())
                } else {
                    return feature.getGeometry().getExtent()
                }
            }, null)

            var center = olExtent.getCenter(totalExtend);

            var size = this.mapModel.map.getSize();

            var mostLeftCoords = this.mapModel.map.getCoordinateFromPixel([0, 0]);
            var mostRightCoords = this.mapModel.map.getCoordinateFromPixel([size[0], 0]);

            var offset = (mostRightCoords[0] - mostLeftCoords[0]) / 6

            var view = this.mapModel.map.getView();

            view.animate({
                center: [center[0] - offset, center[1]],
                duration: 100 // duration in milliseconds
            });
        }
    }

    combineFeatures() {
        this.pointSource.clear();
        this.lineSource.clear();
        this.selectSource.clear();
        this.connectedSource.clear();

        var featureDrawer = new FeatureDrawer(this.projection, this.mapModel.projection);

        var lastSegment = null;
        var allPoints = [];

        this.motionplan.data.forEach((segment) => {
            if (lastSegment) {
                if (segment.Curvature != 0) {

                    allPoints.push(...this.generateArc(segment).map(point => {
                        let vertex = { x: point[0], y: point[1] }
                        if (this.projection != null) {
                            vertex = featureDrawer.transformCoordinates(vertex);
                        }
                        // selectLinePoints.push([vertex.x, vertex.y])
                        return [vertex.x, vertex.y]
                    }))

                } else {
                    let end = { x: segment.X, y: segment.Y };
                    if (this.projection != null) {
                        end = featureDrawer.transformCoordinates(end);
                    }
                    allPoints.push([end.x, end.y]);
                }
            } else {
                let start = { x: segment.X, y: segment.Y };
                if (this.projection != null) {
                    start = featureDrawer.transformCoordinates(start);
                }
                allPoints.push([start.x, start.y]);
            }

            lastSegment = segment;
        });

        if (allPoints.length > 1) {
            const combinedLineString = new LineString(allPoints)
            const combinedFeature = new Feature(combinedLineString);
            combinedFeature.set("motionplan", this.motionplan);
            this.connectedSource.addFeature(combinedFeature);
        } else {
            const point = new Point(allPoints[0])
            const combinedFeature = new Feature(point);
            combinedFeature.set("motionplan", this.motionplan);
            this.connectedSource.addFeature(combinedFeature);
        }
    }

    generateArc(segment) {
        const arc = [];
        const clockwise = segment.Curvature < 0;

        var startAngle, endAngle;

        if (clockwise) {
            startAngle = segment.SegmStartAngle + (Math.PI / 2);
            endAngle = segment.SegmEndAngle + (Math.PI / 2);
        } else {
            startAngle = segment.SegmStartAngle - (Math.PI / 2);
            endAngle = segment.SegmEndAngle - (Math.PI / 2);
        }

        startAngle = this.normalizeAngle(startAngle);
        endAngle = this.normalizeAngle(endAngle);

        var r = Math.sqrt(Math.pow(segment.CentreX - segment.X, 2) + Math.pow(segment.CentreY - segment.Y, 2));

        const differenceAngle = this.calculateArcSweep(startAngle, endAngle, clockwise);

        //gives the length of the arc in meters
        const arcLength = Math.abs(r) * differenceAngle; // r * angle (radians)
        const segmentsByDegree = Math.max(1, Math.round(differenceAngle * (180.0 / Math.PI))); // amount of segments if we do one segment per degree
        const segmentsByLength = Math.max(1, Math.round(arcLength / 1)); // amount of segments if we do segmenst of ARC_SEGMENT_LENGTH_METE

        // Choose the greater one
        const numSegments = Math.max(segmentsByDegree, segmentsByLength);

        //Adds start with the start point. With large radii the start angle is not precise enough. The previeus segment is already added to the start of the arc
        //So in the loop we skip on and stop one earlier.
        for (let i = 1; i <= numSegments - 1; ++i) { // <= to include endpoint
            const t = i / numSegments;
            const angleStep = differenceAngle * t;
            var rotatedAngle;
            if (clockwise) {
                rotatedAngle = startAngle - angleStep;
            } else {
                rotatedAngle = startAngle + angleStep;
            }
            const x = segment.CentreX + (r * Math.cos(rotatedAngle));
            const y = segment.CentreY + (r * Math.sin(rotatedAngle));

            arc.push([x, y]);
        }
        //Add the end point as last.
        arc.push([segment.X, segment.Y]);
        return arc;
    }

    normalizeAngle(angle) {
        angle = angle % (2.0 * Math.PI);
        if (angle < 0) {
            angle += 2.0 * Math.PI;
        }
        return angle;
    }

    calculateArcSweep(startAngle, endAngle, clockwise) {
        startAngle = this.normalizeAngle(startAngle);

        endAngle = this.normalizeAngle(endAngle);

        let sweep;
        if (clockwise) {
            sweep = startAngle - endAngle;
            if (sweep < 0) sweep += 2.0 * Math.PI;
        } else {
            sweep = endAngle - startAngle;
            if (sweep < 0) sweep += 2.0 * Math.PI;
        }

        return sweep;
    }
}